import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Check } from '@osd/icons/check';
import { Info } from '@osd/icons/info';
import { AlertTriangle } from '@osd/icons/alert-triangle';
import { User } from '@osd/icons/user';
import { Pill } from '@osd/ui/pill';
import { Stack } from '@osd/ui/stack';
import { Table } from '@osd/ui/table';
import { Text } from '@osd/ui/text';
import { Button } from '@osd/ui/button';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/pill — live docs for the `Pill` primitive.
 *
 * Pill is the colored inline tag — status chips, role indicators,
 * count badges, filter chips, and feature flags. For the toggle
 * boolean case use `<Switch>`; for quiet monochrome tags use
 * `<Label>`; for non-semantic numerical counts use `<Badge>`.
 */
export const Route = createFileRoute('/ui/_layout/pill')({
  component: PillPage,
});

function PillPage() {
  return (
    <Stack gap={6}>
      <Section
        title="Pill"
        hint="Colored status / count / tag chip — 6 variants × 2 sizes × optional icon × optional dismiss"
      >
        <Text>
          Use Pill when the tag carries semantic intent (role, status, severity). The 6 variants are
          all token-driven via <code>color-mix()</code>; switching <code>--osd-color-*</code> tokens
          re-themes every pill at once.
        </Text>
      </Section>

      <Section title="When to use which?" hint="Pill vs Label vs Badge — decision matrix">
        <Table columns="1fr 2fr 2fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Primitive</Table.Head>
              <Table.Head>Use when…</Table.Head>
              <Table.Head>Example</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <strong>Pill</strong>
              </Table.Cell>
              <Table.Cell>
                The tag carries semantic intent — status, role, severity, count. Choose the variant
                by meaning, not aesthetics.
              </Table.Cell>
              <Table.Cell>
                <Stack direction="row" gap={1}>
                  <Pill variant="success">active</Pill>
                  <Pill variant="warning">pending</Pill>
                  <Pill variant="danger">error</Pill>
                </Stack>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>Label</strong>
              </Table.Cell>
              <Table.Cell>
                Quiet monochrome metadata — categories, prefixes, dates. No semantic color; inherits
                from surrounding context.
              </Table.Cell>
              <Table.Cell>
                <Text>
                  <code>&lt;Label icon={'{<TagIcon />}'}&gt;bug&lt;/Label&gt;</code> opened by alice
                  3 days ago
                </Text>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>Badge</strong>
              </Table.Cell>
              <Table.Cell>
                Non-semantic numerical count — inbox indicators, notification counters. No variant;
                the number is the meaning.
              </Table.Cell>
              <Table.Cell>
                <code>&lt;Badge value={'{3}'} /&gt;</code> next to a nav item
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>

      <Section title="Variants" hint="neutral / info / success / warning / danger / accent">
        <Stack direction="row" gap={2}>
          <Pill variant="neutral">neutral</Pill>
          <Pill variant="info">info</Pill>
          <Pill variant="success">active</Pill>
          <Pill variant="warning">pending</Pill>
          <Pill variant="danger">error</Pill>
          <Pill variant="accent">new</Pill>
        </Stack>
      </Section>

      <Section title="Sizes" hint="sm / md">
        <Stack direction="row" gap={2} align="center">
          <Pill size="sm">small</Pill>
          <Pill size="md">medium</Pill>
          <Pill variant="success" size="sm">
            3
          </Pill>
          <Pill variant="danger" size="md">
            12
          </Pill>
        </Stack>
      </Section>

      <Section title="With icon" hint="Leading icon, 12–16px recommended">
        <Stack direction="row" gap={2}>
          <Pill variant="success" icon={<CheckIcon />}>
            verified
          </Pill>
          <Pill variant="info" icon={<InfoIcon />}>
            beta
          </Pill>
          <Pill variant="warning" icon={<WarnIcon />}>
            deprecated
          </Pill>
        </Stack>
      </Section>

      <Section title="Dismissible" hint="Filter-chip pattern — × button + onDismiss">
        <FilterChipsDemo />
      </Section>

      <Section
        title="Real-world composition"
        hint="How Pills actually appear in tables, user lists, and filter bars"
      >
        <Stack gap={5}>
          <Stack gap={2}>
            <h4 style={{ margin: 0, fontSize: 'var(--osd-text-md)', fontWeight: 600 }}>
              Status pill in a table cell
            </h4>
            <Text muted>
              Pair a Pill with a Table.Cell to show row status. The semantic variant tells the user
              what the row means, not just what it looks like.
            </Text>
            <Table columns="2fr 1fr 1fr">
              <Table.Header>
                <Table.Row>
                  <Table.Head>User</Table.Head>
                  <Table.Head>Role</Table.Head>
                  <Table.Head>Status</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>alice@bugfix.pro</Table.Cell>
                  <Table.Cell>
                    <Pill variant="accent" size="sm">
                      admin
                    </Pill>
                  </Table.Cell>
                  <Table.Cell>
                    <Pill variant="success" size="sm">
                      active
                    </Pill>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>bob@bugfix.pro</Table.Cell>
                  <Table.Cell>
                    <Pill variant="neutral" size="sm">
                      member
                    </Pill>
                  </Table.Cell>
                  <Table.Cell>
                    <Pill variant="warning" size="sm">
                      pending
                    </Pill>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>carol@bugfix.pro</Table.Cell>
                  <Table.Cell>
                    <Pill variant="neutral" size="sm">
                      member
                    </Pill>
                  </Table.Cell>
                  <Table.Cell>
                    <Pill variant="danger" size="sm">
                      error
                    </Pill>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Stack>

          <Stack gap={2}>
            <h4 style={{ margin: 0, fontSize: 'var(--osd-text-md)', fontWeight: 600 }}>
              Role pill in a user list row
            </h4>
            <Text muted>
              Inline pill next to a name (with icon) for compact user lists in side panels.
            </Text>
            <Stack gap={1}>
              <Stack direction="row" gap={2} align="center">
                <Pill variant="accent" size="sm" icon={<UserIcon />}>
                  admin
                </Pill>
                <Text>alice@bugfix.pro</Text>
              </Stack>
              <Stack direction="row" gap={2} align="center">
                <Pill variant="neutral" size="sm" icon={<UserIcon />}>
                  member
                </Pill>
                <Text>bob@bugfix.pro</Text>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Basic"
            code={`<Pill variant="success">active</Pill>
<Pill variant="accent" size="sm">new</Pill>
<Pill variant="warning" icon={<CheckIcon />}>beta</Pill>`}
          />
          <CodeBlock
            language="tsx"
            title="Filter chip with dismiss"
            code={`function FilterChip({ label, onRemove }) {
  return (
    <Pill variant="neutral" onDismiss={onRemove}>
      {label}
    </Pill>
  );
}`}
          />
          <CodeBlock
            language="tsx"
            title="Interactive pill — wrap in a <button>"
            code={`// Pill renders as <span> (non-interactive).
// For an interactive toggle, wrap it in a <button>:
//
//   <button onClick={() => setActive(!active)}>
//     <Pill variant={active ? 'success' : 'neutral'}>
//       {active ? 'on' : 'off'}
//     </Pill>
//   </button>
//
// The <button> handles the role + keyboard; the Pill is purely
// visual chrome.`}
          />
        </Stack>
      </Section>

      <Section title="Accessibility" hint="Per-piece a11y guarantees">
        <Table columns="1fr 2fr 2fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Element</Table.Head>
              <Table.Head>Guarantee</Table.Head>
              <Table.Head>Notes</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <code>&lt;Pill&gt;</code>
              </Table.Cell>
              <Table.Cell>
                Renders as <code>&lt;span&gt;</code>
              </Table.Cell>
              <Table.Cell>
                Non-interactive by default. No ARIA role assigned; screen readers announce the
                visible text only. For interactive cases, wrap in <code>&lt;button&gt;</code> (see
                Code example above).
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Dismissible × button</Table.Cell>
              <Table.Cell>
                <code>aria-label</code> defaults to <code>Remove {'{children}'}</code>
              </Table.Cell>
              <Table.Cell>
                Override with the <code>dismissLabel</code> prop when the children aren't a string
                or you want a more descriptive label (e.g. <code>"Clear status filter"</code>).
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Variants</Table.Cell>
              <Table.Cell>Color is supplementary, not the sole signal</Table.Cell>
              <Table.Cell>
                A <code>variant="danger"</code> pill alone doesn't tell a colourblind user it's an
                error. Pair with an icon (<code>{'<AlertTriangle />'}</code>) or include the word
                ("error", "failed") in the label.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>
    </Stack>
  );
}

function FilterChipsDemo(): JSX.Element {
  const initial = [
    { id: 'status', label: 'status: open' },
    { id: 'owner', label: 'owner: me' },
    { id: 'label', label: 'label: bug' },
  ];
  const [chips, setChips] = useState(initial);

  return (
    <Stack gap={3}>
      <Stack direction="row" gap={2}>
        {chips.map((c) => (
          <Pill
            key={c.id}
            variant="neutral"
            onDismiss={() => setChips((cs) => cs.filter((x) => x.id !== c.id))}
          >
            {c.label}
          </Pill>
        ))}
        {chips.length === 0 && <Text>No filters applied.</Text>}
      </Stack>
      {chips.length > 0 && <Button onClick={() => setChips(initial)}>Reset filters</Button>}
    </Stack>
  );
}

const CheckIcon = Check;
const InfoIcon = Info;
const WarnIcon = AlertTriangle;
const UserIcon = User;
